import Button from '@material-ui/core/Button';
import Dialog from '@material-ui/core/Dialog';
import MuiDialogActions from '@material-ui/core/DialogActions';
import MuiDialogContent from '@material-ui/core/DialogContent';
import MuiDialogTitle from '@material-ui/core/DialogTitle';
import IconButton from '@material-ui/core/IconButton';
import MenuItem from '@material-ui/core/MenuItem';
import Typography from '@material-ui/core/Typography';
import ArrowForwardIosIcon from '@material-ui/icons/ArrowForwardIos';
import CloseIcon from '@material-ui/icons/Close';
import uploadImg from 'assets/upload.png';
import format from 'date-fns/format';
import * as FileSaver from 'file-saver';
import isEmpty from 'lodash/isEmpty';
import uniq from 'lodash/uniq';
import WIRField from 'pages/WellGuardian/WIR/components/WIRField';
import { getRoleName } from 'pages/WellGuardian/WIR/utils';
import { getAssetsByCountry } from 'pages/WellGuardian/WIR/utils/getAssetsByCountry';
import React, { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { useCountryState } from 'redux/WellGuardian/selectors';
import userApiClient from 'services/networking/userApiClient';
import XLSX from 'sheetjs-style';
import { UserDTO } from 'types/user';
import {
  AssetsAg,
  AssetsDk,
  AssetsNg,
  AssetsUk,
  WIRTransverseRoles,
  WIRUser,
} from 'types/wellGuardian';

import styles from './UserDetailsModal.module.scss';
import { UserMode } from '../../userRoleSettingsModal.constants';
import { getAllWirRoles } from '../../utils';

const ALL_WIR = 'ALL';

const fileType = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8';

const fileExtension = '.xlsx';

const PDF_DATE_FORMAT = 'yyyy-MM-dd';
interface UserDetailsModalProps {
  modalOpen: boolean;
  okMethod: () => void;
  cancelMethod: () => void;
  setUser: (user: WIRUser) => void;
  user: WIRUser;
  mode: UserMode | undefined;
  isSuperUser: boolean;
  handleFileUpload: (event: React.ChangeEvent<HTMLInputElement>) => void;
  multiUser: boolean;
  setMultiUser: (value: boolean) => void;
}

const getFieldValue = (value: string | undefined) => value ?? '';

const getAssetFilter = (user: WIRUser, country: string) => {
  const countryAsset = getAssetsByCountry(country);

  const handleCursor = (user: any, eventKey: any) => {
    if (user.assets?.includes(eventKey)) {
      return 'pointer';
    }

    return user.assets?.includes('ALL') ? 'not-allowed' : 'pointer';
  };

  const handleColor = (user: any, eventKey: any) => {
    if (user.assets?.includes(eventKey)) {
      return styles.enableClr;
    }

    return user.assets?.includes('ALL') ? styles.disableClr : styles.enableClr;
  };

  return Object.keys(countryAsset).map((eventKey, idx) => (
    <MenuItem
      dense
      value={eventKey}
      key={eventKey}
      style={{
        background: user.assets?.includes(eventKey) ? styles.selectedMenuColor : undefined,
        cursor: handleCursor(user, eventKey),
        color: handleColor(user, eventKey),
      }}
    >
      {Object.values(countryAsset)[idx]}
    </MenuItem>
  ));
};

const UserDetailsModal: React.FunctionComponent<UserDetailsModalProps> = ({
  modalOpen,
  okMethod,
  cancelMethod,
  setUser,
  user,
  mode,
  isSuperUser,
  handleFileUpload,
  multiUser,
  setMultiUser,
}) => {
  const intl = useIntl();
  const countrySelect = useCountryState();
  const [userData, setUserData] = useState<UserDTO[]>([]);

  // fetch user data from API
  useEffect(() => {
    const fetchUserData = async () => {
      const response = await userApiClient.getUsers();

      setUserData(response);
    };

    fetchUserData();
  }, []);

  useEffect(() => {
    if (user.emailAddress) {
      const filteredUser = userData.find(
        u => u.email?.toLowerCase() === user.emailAddress?.toLowerCase(),
      );
      if (filteredUser) {
        setUser({
          ...user,
          userId: filteredUser.igg,
          userName: filteredUser.firstName + ' ' + filteredUser.lastName,
        });
      }
    }
  }, [setUser, user, user.emailAddress, userData]);

  const getAllRequiredFieldsFilled = () =>
    !isEmpty(user.userId) &&
    !isEmpty(user.userName) &&
    !isEmpty(user.emailAddress) &&
    !isEmpty(user.roles);

  const handelAssetChange = (value: any) => {
    if (value.includes(ALL_WIR) && value.length !== 0) {
      value = [ALL_WIR];
    }

    return setUser({
      ...user,
      assets: value as AssetsUk[] | AssetsDk[] | AssetsNg[] | AssetsAg[],
    });
  };

  const handleMultipleUsersPopup = () => {
    setMultiUser(true);
  };

  const additionData = [
    {
      emailAddress: '',
      userId: '',
      userName: '',
    },
  ];

  const exportToExcel = async () => {
    const ws = XLSX.utils.json_to_sheet(additionData);
    const wb = { Sheets: { data: ws }, SheetNames: ['data'] };
    const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    const data = new Blob([excelBuffer], { type: fileType });
    FileSaver.saveAs(
      data,
      `MULTIPLE USER ROLES - ${format(new Date(), PDF_DATE_FORMAT)}` + fileExtension,
    );
  };

  return (
    <Dialog
      aria-labelledby="user-details-dialog"
      open={modalOpen}
      maxWidth="md"
      scroll="body"
      classes={{ paperWidthMd: styles.paperWidthMd, root: styles.root }}
      data-testid="user-details-dialog"
    >
      <MuiDialogTitle disableTypography className={styles.titleDialog}>
        <Typography variant="h6" className={styles.titleText}>
          {mode === 'add' &&
            !multiUser &&
            intl.formatMessage({ id: 'wellGuardian.wir.userRoleSettings.addNewUser' })}
          {mode === 'edit' &&
            !multiUser &&
            intl.formatMessage({ id: 'wellGuardian.wir.userRoleSettings.editUser' })}
          {multiUser &&
            intl.formatMessage({ id: 'wellGuardian.wir.userRoleSettings.addMultipleUser' })}
        </Typography>
        <IconButton aria-label="close" className={styles.closeButton} onClick={cancelMethod}>
          <CloseIcon />
        </IconButton>
      </MuiDialogTitle>
      {!multiUser ? (
        <>
          <MuiDialogContent classes={{ root: styles.contentRoot }}>
            <WIRField
              id="outlined-emailAddress-field"
              label={intl.formatMessage({ id: 'wellGuardian.wir.userRoleSettings.emailAddress' })}
              type="search"
              value={getFieldValue(user.emailAddress?.toLowerCase())}
              onChange={event =>
                setUser({ ...user, emailAddress: event.target.value?.toLowerCase() })
              }
              disabled={mode === 'edit'}
              required
              validateEmail={true}
            />
            <WIRField
              id="outlined-userId-field"
              label={intl.formatMessage({ id: 'wellGuardian.wir.userRoleSettings.userId' })}
              type="search"
              value={getFieldValue(user.userId)}
              onChange={event => setUser({ ...user, userId: event.target.value })}
              disabled={mode === 'edit'}
              required
            />
            <WIRField
              id="outlined-userName-field"
              label={intl.formatMessage({ id: 'wellGuardian.wir.userRoleSettings.name' })}
              type="search"
              value={getFieldValue(user.userName)}
              onChange={event => setUser({ ...user, userName: event.target.value })}
              required
            />
            <WIRField
              id="outlined-asset-field"
              label={intl.formatMessage({ id: 'wellGuardian.wir.userRoleSettings.asset' })}
              SelectProps={{
                MenuProps: {
                  getContentAnchorEl: null,
                },
                multiple: true,
                value: user.assets ?? [],
                onChange: event => {
                  handelAssetChange(event?.target?.value);
                },
              }}
              select
            >
              {getAssetFilter(user, countrySelect)}
            </WIRField>
            <WIRField
              id="outlined-roles-field"
              label={intl.formatMessage({ id: 'wellGuardian.wir.userRoleSettings.roles' })}
              SelectProps={{
                MenuProps: {
                  getContentAnchorEl: null,
                },
                multiple: true,
                value: user.roles ?? [],
                onChange: event =>
                  setUser({
                    ...user,
                    roles: uniq([
                      WIRTransverseRoles.READ_ONLY_WIR_USER,
                      ...(event.target.value as WIRTransverseRoles[]),
                    ]),
                  }),
              }}
              select
              required
            >
              {getAllWirRoles().map(role => (
                <MenuItem
                  dense
                  value={role}
                  key={role}
                  style={{
                    background: user.roles?.includes(role as WIRTransverseRoles)
                      ? styles.selectedMenuColor
                      : undefined,
                  }}
                >
                  {getRoleName(role)}
                </MenuItem>
              ))}
            </WIRField>
          </MuiDialogContent>
          <div className={styles.btnRow}>
            {isSuperUser && (
              <div className={styles.fileUploads}>
                <div className={styles.customFileUpload} onClick={handleMultipleUsersPopup}>
                  <img src={uploadImg} className={styles.uploadImg} alt="" />
                  ADD MULTIPLE USERS
                </div>
              </div>
            )}
            <MuiDialogActions>
              <Button onClick={cancelMethod} className={styles.cancelButton}>
                {intl.formatMessage({
                  id: 'wellGuardian.wir.createNewWIRModal.cancel',
                })}
              </Button>
              <Button
                onClick={okMethod}
                className={styles.okButton}
                disabled={!getAllRequiredFieldsFilled()}
              >
                {intl.formatMessage({
                  id: 'wellGuardian.wir.createNewWIRModal.saveAndExit',
                })}
                <ArrowForwardIosIcon fontSize="small" className={styles.buttonIcon} />
              </Button>
            </MuiDialogActions>
          </div>
        </>
      ) : (
        <div className={styles.excelMain}>
          <p>
            A super user can add multiple users by first downloading an Excel template from
            <span data-testid="export" onClick={exportToExcel} className={styles.hereBtn}>
              here,
            </span>
            filling in the required details, and then uploading the completed file.
          </p>
          <div className={styles.fileUpload}>
            <label htmlFor="file-upload">
              <div className={styles.customFileUpload} onClick={handleMultipleUsersPopup}>
                <img src={uploadImg} className={styles.uploadImg} alt="" />
                UPLOAD
              </div>
            </label>
            <input
              id="file-upload"
              type="file"
              onChange={handleFileUpload}
              accept=".xlsx"
              style={{ display: 'none' }}
            />
          </div>
        </div>
      )}
    </Dialog>
  );
};

export default UserDetailsModal;
